// app_client/src/store/useLocationStore.js
import { create } from 'zustand';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const collectionByType = {
  country: 'countries',
  city: 'cities',
  district: 'districts'
};

const useLocationStore = create((set) => ({
  // Veri Setleri
  countries: [],
  cities: [],
  allCities: [],
  districts: [],

  // Loading Durumları
  loading: {
    countries: false,
    cities: false,
    districts: false,
    action: false // Ekleme/Silme işlemleri için
  },

  // --- GET İŞLEMLERİ ---

  // 1. Tüm Ülkeleri Getir
  fetchCountries: async () => {
    set((state) => ({ loading: { ...state.loading, countries: true } }));
    try {
      const { data } = await axios.get('/api/locations/countries');
      set({ countries: data });
    } catch {
      toast.error("Ülkeler yüklenemedi");
    } finally {
      set((state) => ({ loading: { ...state.loading, countries: false } }));
    }
  },

  fetchAllCities: async () => {
    set((state) => ({ loading: { ...state.loading, cities: true } }));
    try {
      const { data } = await axios.get('/api/locations/cities');
      set({ allCities: data });
    } catch {
      toast.error("Şehirler yüklenemedi");
    } finally {
      set((state) => ({ loading: { ...state.loading, cities: false } }));
    }
  },

  // 2. Seçilen Ülkeye Göre Şehirleri Getir
  fetchCities: async (countryId) => {
    if (!countryId) return;
    set((state) => ({ 
      loading: { ...state.loading, cities: true },
      districts: [] // Ülke değişince ilçeleri temizle
    }));
    try {
      const { data } = await axios.get(`/api/locations/cities/${countryId}`);
      set({ cities: data });
    } catch {
      toast.error("Şehirler yüklenemedi");
    } finally {
      set((state) => ({ loading: { ...state.loading, cities: false } }));
    }
  },

  // 3. Seçilen Şehre Göre İlçeleri Getir
  fetchDistricts: async (cityId) => {
    if (!cityId) return;
    set((state) => ({ loading: { ...state.loading, districts: true } }));
    try {
      const { data } = await axios.get(`/api/locations/districts/${cityId}`);
      set({ districts: data });
    } catch {
      toast.error("İlçeler yüklenemedi");
    } finally {
      set((state) => ({ loading: { ...state.loading, districts: false } }));
    }
  },

  // --- POST İŞLEMLERİ (Tanımlamalar İçin) ---

  addCountry: async (countryData) => {
    set((state) => ({ loading: { ...state.loading, action: true } }));
    try {
      const { data } = await axios.post('/api/locations/countries', countryData);
      set((state) => ({ countries: [...state.countries, data] }));
      toast.success("Ülke başarıyla eklendi");
      return data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Ülke eklenemedi");
    } finally {
      set((state) => ({ loading: { ...state.loading, action: false } }));
    }
  },

  addCity: async (countryId, cityData) => {
    set((state) => ({ loading: { ...state.loading, action: true } }));
    try {
      const { data } = await axios.post(`/api/locations/cities/${countryId}`, cityData);
      set((state) => ({ cities: [...state.cities, data] }));
      toast.success("Şehir başarıyla eklendi");
      return data;
    } catch {
      toast.error("Şehir eklenemedi");
    } finally {
      set((state) => ({ loading: { ...state.loading, action: false } }));
    }
  },

  addDistrict: async (cityId, districtData) => {
    set((state) => ({ loading: { ...state.loading, action: true } }));
    try {
      const { data } = await axios.post(`/api/locations/districts/${cityId}`, districtData);
      set((state) => ({ districts: [...state.districts, data] }));
      toast.success("İlçe/Bölge başarıyla eklendi");
      return data;
    } catch {
      toast.error("İlçe eklenemedi");
    } finally {
      set((state) => ({ loading: { ...state.loading, action: false } }));
    }
  },

  updateLocation: async (id, locationData) => {
    set((state) => ({ loading: { ...state.loading, action: true } }));
    try {
      const { data } = await axios.put(`/api/locations/${id}`, locationData);
      const collectionName = collectionByType[data.type];

      if (collectionName) {
        set((state) => ({
          [collectionName]: state[collectionName].map((item) => (
            item._id === id ? data.data : item
          ))
        }));
      }

      toast.success("Lokasyon güncellendi");
      return { success: true, ...data };
    } catch (error) {
      toast.error(error.response?.data?.message || "Lokasyon güncellenemedi");
      return { success: false };
    } finally {
      set((state) => ({ loading: { ...state.loading, action: false } }));
    }
  },

  deleteLocation: async (id) => {
    set((state) => ({ loading: { ...state.loading, action: true } }));
    try {
      const { data } = await axios.delete(`/api/locations/${id}`);
      const collectionName = collectionByType[data.type];

      if (collectionName) {
        set((state) => ({
          [collectionName]: state[collectionName].filter((item) => item._id !== id)
        }));
      }

      toast.success(data.message || "Lokasyon silindi");
      return { success: true, ...data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Beklenmedik bir hata oluştu.";
      toast.error(errorMessage, { duration: 6000 });
      console.error("Detaylı Hata:", error);
      return { success: false };
    } finally {
      set((state) => ({ loading: { ...state.loading, action: false } }));
    }
  }
}));

export default useLocationStore;
